import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Notification state interface
 * Manages active notification classNames for display control
 */
export interface NotificationState {
  notificationsActive: string[];
}

const initialState: NotificationState = {
  notificationsActive: [],
};

/**
 * Notification slice for managing active notifications
 *
 * This slice handles notification display state by tracking active notification
 * classNames. Only one notification is shown at a time (the latest one).
 *
 * @example
 * ```typescript
 * // Add notification
 * dispatch(addNotification('success-notification'));
 *
 * // Remove notification
 * dispatch(removeNotification('success-notification'));
 *
 * // Clear all notifications
 * dispatch(clearAllNotifications());
 * ```
 */
const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    /**
     * Add a notification className to the active notifications list
     * @param state - Current notification state
     * @param action - Payload containing the notification className
     */
    addNotification: (state, action: PayloadAction<string>) => {
      state.notificationsActive.push(action.payload);
    },

    /**
     * Remove a notification className from the active notifications list
     * @param state - Current notification state
     * @param action - Payload containing the notification className to remove
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notificationsActive = state.notificationsActive.filter(
        (item) => item !== action.payload,
      );
    },

    /**
     * Clear all active notifications
     * @param state - Current notification state
     */
    clearAllNotifications: (state) => {
      state.notificationsActive = [];
    },
  },
});

export const { addNotification, removeNotification } = notificationSlice.actions;

export default notificationSlice.reducer;
